import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, Navigation, Wallet, CreditCard, ShieldCheck, Clock, Calculator, Truck, CheckCircle2, Stethoscope, CalendarCheck, Route, Users } from "lucide-react";
import { useAuth } from "../auth";
import "../landing.css";

const FEATURES = [
  { icon: Calculator, title: "Orçamento automático", desc: "Monte orçamentos de remoção em minutos com custos de combustível, equipe, pedágio e sua margem — sem planilhas.", tag: "Rápido e preciso" },
  { icon: MapPin, title: "Rota e pedágio via Google Maps", desc: "Distância, tempo e valores de pedágio calculados automaticamente pela API oficial do Google Maps, por eixo do veículo.", tag: "Precisão operacional" },
  { icon: Navigation, title: "Rastreamento do motorista ao vivo", desc: "Envie um link com GPS para o motorista e acompanhe a viagem em tempo real no mapa interativo. Ele navega, você acompanha.", tag: "Link vivo GPS" },
  { icon: Wallet, title: "Financeiro por remoção realizada", desc: "Receitas, custos e lucro de cada remoção realizada, com relatórios mensais e faturamento organizados.", tag: "Gestão completa" },
];

const CLINIC_BENEFITS = [
  { icon: Route, title: "Traslados de pacientes organizados", desc: "Planeje os traslados da sua frota própria entre casa do paciente e clínica, com rota, distância e pedágio calculados automaticamente." },
  { icon: CalendarCheck, title: "Agenda de transportes em um só lugar", desc: "Veja todos os traslados agendados, realizados e pendentes do mês, sem depender de planilhas ou grupos de mensagens." },
  { icon: Navigation, title: "Saiba onde o veículo está", desc: "Envie o link de rastreamento ao motorista e acompanhe a chegada ao paciente e à clínica em tempo real." },
  { icon: Users, title: "Pacientes e equipe cadastrados", desc: "Mantenha pacientes, responsáveis, veículos e profissionais da sua clínica organizados e com acesso por perfil." },
];

const STEPS = [
  { n: "1", title: "Cadastre sua empresa", desc: "Crie a conta com 14 dias grátis e configure veículos, equipe e valores." },
  { n: "2", title: "Monte o orçamento", desc: "Informe origem, paciente e clínica — o sistema calcula rota, pedágio e preço." },
  { n: "3", title: "Envie e agende", desc: "Envie o orçamento por WhatsApp/PDF e converta em remoção agendada." },
  { n: "4", title: "Rastreie e cobre", desc: "Acompanhe o motorista ao vivo e feche o mês com o financeiro em dia." },
];

function AccessCard({ mode, setMode }) {
  const { login, registerCompany, formatApiErrorDetail } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [f, setF] = useState({ email: "", password: "", companyName: "", adminName: "", phone: "", segment: "remocao" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const u = mode === "login"
        ? await login(f.email, f.password)
        : await registerCompany({ companyName: f.companyName, adminName: f.adminName, email: f.email, password: f.password, phone: f.phone, segment: f.segment });
      nav(u.role === "superadmin" ? "/admin" : "/", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Não foi possível concluir. Tente novamente.");
    } finally { setLoading(false); }
  };

  return (
    <div className="lp-access" id="acesso" data-testid="hero-access-card">
      <div className="lp-access-tabs">
        <button className={mode === "login" ? "on" : ""} onClick={() => { setMode("login"); setError(""); }} data-testid="access-tab-login">Entrar</button>
        <button className={mode === "register" ? "on" : ""} onClick={() => { setMode("register"); setError(""); }} data-testid="access-tab-register">Faça seu cadastro</button>
      </div>
      <h3>{mode === "login" ? "Entrar na plataforma" : "Criar conta da empresa"}</h3>
      <p className="lp-access-sub">{mode === "login" ? "Acesse a operação da sua empresa." : "Comece com 14 dias grátis. Sem cartão."}</p>
      {error && <div className="lp-error" data-testid="access-error">{error}</div>}
      <form onSubmit={submit}>
        {mode === "register" && (
          <>
            <label className="lp-field"><span>Nome da empresa</span><input value={f.companyName} onChange={set("companyName")} required placeholder="Ex.: Remoções Vida" data-testid="lp-reg-company" /></label>
            <label className="lp-field"><span>Seu nome (administrador)</span><input value={f.adminName} onChange={set("adminName")} required placeholder="Nome do responsável" data-testid="lp-reg-name" /></label>
            <label className="lp-field"><span>Segmento da empresa</span>
              <select value={f.segment} onChange={set("segment")} data-testid="lp-reg-segment">
                <option value="remocao">Empresa de remoção / ambulância</option>
                <option value="clinica">Clínica com frota (traslados)</option>
              </select>
            </label>
            <label className="lp-field"><span>Telefone / WhatsApp</span><input value={f.phone} onChange={set("phone")} placeholder="(11) 99999-9999" data-testid="lp-reg-phone" /></label>
          </>
        )}
        <label className="lp-field"><span>E-mail profissional</span><input type="email" value={f.email} onChange={set("email")} required placeholder="voce@empresa.com.br" data-testid="lp-email" /></label>
        <label className="lp-field"><span>Senha</span><input type="password" value={f.password} onChange={set("password")} required placeholder="Sua senha" data-testid="lp-password" /></label>
        <button className="lp-btn lp-btn-green lp-btn-block" type="submit" disabled={loading} data-testid="lp-submit">
          {loading ? "Aguarde…" : mode === "login" ? "Entrar" : "Criar conta e começar"}
        </button>
      </form>
      {mode === "login"
        ? <div className="lp-access-alt">Não tem conta? <a onClick={() => setMode("register")} style={{ cursor: "pointer" }} data-testid="lp-goto-register">Faça seu cadastro</a> · <Link to="/forgot-password">Esqueci a senha</Link></div>
        : <div className="lp-trust">Sem necessidade de cartão nos primeiros 14 dias.</div>}
    </div>
  );
}

function CookieBanner() {
  const [show, setShow] = useState(false);
  useEffect(() => { setShow(!localStorage.getItem("rs_cookie_consent")); }, []);
  const accept = (v) => { localStorage.setItem("rs_cookie_consent", v); setShow(false); };
  if (!show) return null;
  return (
    <div className="lp-cookie" data-testid="cookie-consent-banner">
      <p>Usamos cookies essenciais para o funcionamento da plataforma e cookies de preferências para melhorar sua experiência, conforme a LGPD. Saiba mais na <Link to="/cookies" data-testid="cookie-view-policy">Política de Cookies</Link>.</p>
      <div className="lp-cookie-actions">
        <button className="lp-btn lp-btn-ghost" onClick={() => accept("necessary")} data-testid="cookie-accept-necessary">Apenas necessários</button>
        <button className="lp-btn lp-btn-green" onClick={() => accept("all")} data-testid="cookie-accept-all">Aceitar todos</button>
      </div>
    </div>
  );
}

export default function Home() {
  const [mode, setMode] = useState("login");
  const nav = useNavigate();
  const goTo = (m) => { setMode(m); document.getElementById("acesso")?.scrollIntoView({ behavior: "smooth", block: "center" }); };

  return (
    <div className="lp">
      <header className="lp-header" data-testid="landing-header">
        <div className="lp-container lp-header-in">
          <div className="lp-logo"><img src="/rotasaude-logo.png" alt="RotaSaúde" /></div>
          <nav className="lp-nav">
            <a className="link lp-nav-mobile-hide" href="#recursos">Funcionalidades</a>
            <a className="link lp-nav-mobile-hide" href="#como-funciona">Como funciona</a>
            <a className="link lp-nav-mobile-hide" href="#planos">Planos</a>
            <button className="lp-btn lp-btn-ghost" onClick={() => nav("/login")} data-testid="nav-login-btn">Entrar</button>
            <button className="lp-btn lp-btn-green" onClick={() => goTo("register")} data-testid="nav-register-btn">Faça seu cadastro</button>
          </nav>
        </div>
      </header>

      <div className="lp-hero-banner" data-testid="hero-banner">
        <img src="/rotasaude-banner.webp" alt="RotaSaúde — Planejamento que leva cuidado mais longe. Transporte e remoção de pacientes." />
      </div>

      <section className="lp-hero" data-testid="hero-section">
        <div className="lp-container lp-hero-grid">
          <div className="lp-hero-copy">
            <span className="lp-badge"><ShieldCheck size={15} /> Gestão inteligente de remoções médicas & ambulâncias</span>
            <h1>Sua empresa de remoção mais eficiente, lucrativa e sob controle</h1>
            <p className="lp-sub">Orçamentos automáticos com distância e pedágio reais do Google Maps, rastreamento do motorista em tempo real e financeiro completo — tudo em um só sistema, em nuvem e multiempresa.</p>
            <div className="lp-hero-ctas">
              <button className="lp-btn lp-btn-green" onClick={() => goTo("register")} data-testid="hero-cta-signup"><Truck size={17} /> Testar 14 dias grátis</button>
              <button className="lp-btn lp-btn-navy" onClick={() => nav("/login")} data-testid="hero-cta-login">Acessar plataforma</button>
            </div>
            <div className="lp-proof"><CheckCircle2 size={18} color="#27b35f" /> <span>Planejamento que leva <b>cuidado até você</b> — dados isolados por empresa e seguros.</span></div>
          </div>
          <AccessCard mode={mode} setMode={setMode} />
        </div>
      </section>

      <section className="lp-section" id="recursos" data-testid="features-bento-grid">
        <div className="lp-container">
          <div className="lp-eyebrow">Tudo o que sua operação precisa</div>
          <h2>Um sistema feito para transporte e remoção de pacientes</h2>
          <p className="lp-lead" style={{ textWrap: "balance" }}>Do orçamento ao recebimento, com a precisão que a operação de ambulâncias&nbsp;exige.</p>
          <div className="lp-features">
            {FEATURES.map((ft) => (
              <div className="lp-feature" key={ft.title} data-testid={`feature-${ft.title}`}>
                <div className="ico"><ft.icon size={24} /></div>
                <h3>{ft.title}</h3>
                <p>{ft.desc}</p>
                <span className="tag">{ft.tag}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="lp-section alt" id="clinicas" data-testid="clinics-section">
        <div className="lp-container">
          <div className="lp-eyebrow"><Stethoscope size={14} style={{ verticalAlign: "-2px" }} /> Para clínicas com frota própria</div>
          <h2>Sua clínica também controla os traslados dos pacientes</h2>
          <p className="lp-lead" style={{ textWrap: "balance" }}>Clínicas que levam e buscam pacientes com veículos próprios usam o RotaSaúde para organizar rotas, horários e custos de cada&nbsp;traslado.</p>
          <div className="lp-features">
            {CLINIC_BENEFITS.map((b, i) => (
              <div className="lp-feature" key={b.title} data-testid={`clinic-benefit-${i}`}>
                <div className="ico"><b.icon size={24} /></div>
                <h3>{b.title}</h3>
                <p>{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="lp-section" id="como-funciona">
        <div className="lp-container">
          <div className="lp-eyebrow">Simples de usar</div>
          <h2>Como funciona</h2>
          <div className="lp-steps">
            {STEPS.map((s) => (
              <div className="lp-step" key={s.n}>
                <div className="num">{s.n}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="lp-section" id="planos" data-testid="pricing-plans-section">
        <div className="lp-container">
          <div className="lp-eyebrow">Assinatura mensal</div>
          <h2>Comece grátis, assine quando quiser</h2>
          <p className="lp-lead">14 dias de teste sem cartão. Pagamento seguro via Mercado Pago (PIX ou cartão de crédito). Todos os planos incluem rastreamento de motorista em tempo real.</p>
          <div className="lp-plans" data-testid="pricing-plans-grid">
            {[
              { name: "Essencial", price: 199, tag: "", feats: ["Até 3 veículos", "Rastreamento em tempo real", "Até 15 remoções por mês", "Até 3 usuários", "Orçamentos e rotas Google Maps", "Financeiro, PDFs e WhatsApp"] },
              { name: "Profissional", price: 299, tag: "Mais popular", feats: ["Até 5 veículos", "Rastreamento em tempo real", "Até 30 remoções por mês", "Até 5 usuários", "Orçamentos e rotas Google Maps", "Financeiro, PDFs e WhatsApp"] },
              { name: "Premium", price: 399, tag: "Completo", feats: ["Até 10 veículos", "Rastreamento em tempo real", "Remoções ilimitadas", "Até 10 usuários", "Orçamentos e rotas Google Maps", "Suporte prioritário"] },
            ].map((p) => (
              <div className={`lp-plan lp-plan-card ${p.tag === "Mais popular" ? "lp-plan-featured" : ""}`} key={p.name} data-testid={`plan-card-${p.name.toLowerCase()}`}>
                {p.tag && <div className="lp-plan-badge">{p.tag}</div>}
                <div style={{ fontWeight: 700, opacity: .85 }}>Plano {p.name}</div>
                <div className="price">R$ {p.price}<small>/mês</small></div>
                <ul>{p.feats.map((ft) => <li key={ft}>{ft}</li>)}</ul>
                <button className="lp-btn lp-btn-green lp-btn-block" onClick={() => goTo("register")} data-testid={`plan-cta-${p.name.toLowerCase()}`}>Iniciar teste de 14 dias</button>
              </div>
            ))}
          </div>
          <p style={{ marginTop: 18, textAlign: "center", fontSize: 13, opacity: .75 }}><Clock size={13} style={{ verticalAlign: "-2px" }} /> Cancele quando quiser. Faça upgrade ou downgrade a qualquer momento.</p>
        </div>
      </section>

      <footer className="lp-footer" data-testid="landing-footer">
        <div className="lp-container">
          <div className="lp-footer-grid">
          <div className="brand">
            <div className="lp-logo lp-footer-logo"><img src="/rotasaude-logo.png" alt="RotaSaúde" /></div>
            <p>RotaSaúde — plataforma de gestão para empresas de remoção médica e ambulâncias. Tecnologia a serviço da vida.</p>
          </div>
          <div>
            <h4>Plataforma</h4>
            <a href="#recursos">Funcionalidades</a>
            <a href="#como-funciona">Como funciona</a>
            <a href="#planos">Planos</a>
          </div>
          <div>
            <h4>Jurídico (LGPD)</h4>
            <Link to="/privacidade" data-testid="footer-link-privacidade">Política de Privacidade</Link>
            <Link to="/termos" data-testid="footer-link-termos">Termos de Uso</Link>
            <Link to="/confidencialidade" data-testid="footer-link-confidencialidade">Termo de Confidencialidade</Link>
            <Link to="/cookies" data-testid="footer-link-cookies">Política de Cookies</Link>
          </div>
          </div>
          <div className="lp-footer-bottom">
          <span>© {new Date().getFullYear()} RotaSaúde — RAF Rede de Apoio aos Familiares • CNPJ 61.281.627/0001-52. Todos os direitos reservados.</span>
          <span><CreditCard size={13} style={{ verticalAlign: "-2px" }} /> Pagamentos seguros via Mercado Pago</span>
          </div>
        </div>
      </footer>

      <CookieBanner />
    </div>
  );
}
